import React, { useState, useEffect, useCallback } from 'react';
import { crmAPI, usersAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Plus, Users as UsersIcon, Phone, Mail, Calendar as CalendarIcon, Trash2, Pencil, Loader2, ArrowRightCircle, TrendingUp, IndianRupee, Target } from 'lucide-react';
import { toast } from 'sonner';

const STAGES = [
  { key: 'new', label: 'New', color: 'bg-slate-100 text-slate-700 border-slate-300' },
  { key: 'contacted', label: 'Contacted', color: 'bg-sky-100 text-sky-700 border-sky-300' },
  { key: 'qualified', label: 'Qualified', color: 'bg-violet-100 text-violet-700 border-violet-300' },
  { key: 'proposal', label: 'Proposal', color: 'bg-amber-100 text-amber-700 border-amber-300' },
  { key: 'won', label: 'Won', color: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  { key: 'lost', label: 'Lost', color: 'bg-rose-100 text-rose-700 border-rose-300' },
];

const SOURCES = ['website', 'referral', 'instagram', 'linkedin', 'google_ads', 'walk_in', 'other'];
const PROPERTY_TYPES = ['residential', 'commercial', 'retail', 'hospitality', 'office', 'other'];

const fmtINR = (n) => n ? `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '₹0';

export default function CRM() {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [activityNote, setActivityNote] = useState('');
  const [convertName, setConvertName] = useState('');

  const emptyForm = {
    name: '', contact_person: '', email: '', phone: '', source: 'website', stage: 'new',
    estimated_budget: '', estimated_value: '', city: '', property_type: 'residential',
    area_sqft: '', timeline: '', next_follow_up: '', assigned_to: '', tags: '', notes: '',
  };
  const [form, setForm] = useState(emptyForm);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [leadsRes, statsRes, teamRes] = await Promise.all([
        crmAPI.listLeads(), crmAPI.stats(), usersAPI.teamMembers(),
      ]);
      setLeads(leadsRes.data); setStats(statsRes.data); setTeamMembers(teamRes.data);
    } catch (e) { console.error(e); toast.error('Failed to load CRM data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => { setForm(emptyForm); setEditing(null); setDialogOpen(true); };
  const openEdit = (lead) => {
    setEditing(lead);
    setForm({
      ...emptyForm, ...lead,
      tags: (lead.tags || []).join(', '),
      estimated_budget: lead.estimated_budget || '',
      estimated_value: lead.estimated_value || '',
      area_sqft: lead.area_sqft || '',
      next_follow_up: lead.next_follow_up || '',
      assigned_to: lead.assigned_to || '',
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Lead name required'); return; }
    setFormLoading(true);
    try {
      const payload = {
        ...form,
        estimated_budget: parseFloat(form.estimated_budget) || 0,
        estimated_value: parseFloat(form.estimated_value) || 0,
        area_sqft: form.area_sqft ? parseFloat(form.area_sqft) : null,
        tags: typeof form.tags === 'string' ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : form.tags,
      };
      if (editing) { await crmAPI.updateLead(editing.lead_id, payload); toast.success('Lead updated'); }
      else { await crmAPI.createLead(payload); toast.success('Lead created'); }
      setDialogOpen(false); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setFormLoading(false); }
  };

  const handleStageChange = async (lead, newStage) => {
    try {
      await crmAPI.updateLead(lead.lead_id, { stage: newStage });
      toast.success(`Moved to ${newStage}`);
      fetchData();
    } catch (e) { toast.error('Failed to update stage'); }
  };

  const handleDelete = async (lead) => {
    if (!window.confirm(`Delete lead "${lead.name}"?`)) return;
    try { await crmAPI.deleteLead(lead.lead_id); toast.success('Deleted'); fetchData(); }
    catch (e) { toast.error('Delete failed'); }
  };

  const openDetail = async (lead) => {
    try {
      const res = await crmAPI.getLead(lead.lead_id);
      setSelected(res.data); setDetailOpen(true);
    } catch (e) { toast.error('Failed to load lead'); }
  };

  const addActivity = async () => {
    if (!activityNote.trim() || !selected) return;
    try {
      await crmAPI.addActivity(selected.lead_id, { note: activityNote.trim(), action: 'note' });
      setActivityNote('');
      const res = await crmAPI.getLead(selected.lead_id);
      setSelected(res.data);
      toast.success('Activity added');
    } catch (e) { toast.error('Failed'); }
  };

  const handleConvert = async () => {
    if (!selected) return;
    try {
      await crmAPI.convertToProject(selected.lead_id, { name: convertName || `${selected.name} - Project` });
      toast.success('Lead converted to project');
      setConvertOpen(false); setDetailOpen(false); fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Conversion failed'); }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="crm-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-light tracking-tight flex items-center gap-3">
              <Target className="h-7 w-7 text-violet-500" />CRM / Lead Pipeline
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Track prospects from first touch to signed project</p>
          </div>
          <Button onClick={openCreate} data-testid="create-lead-btn"><Plus className="h-4 w-4 mr-2" />New Lead</Button>
        </div>

        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="crm-stats">
            <Card><CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground text-xs"><UsersIcon className="h-3.5 w-3.5" />Open Leads</div>
              <div className="text-2xl font-light mt-1">{stats.total_open_leads}</div>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground text-xs"><IndianRupee className="h-3.5 w-3.5" />Pipeline Value</div>
              <div className="text-2xl font-light mt-1">{fmtINR(stats.total_open_value)}</div>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground text-xs"><TrendingUp className="h-3.5 w-3.5" />Won</div>
              <div className="text-2xl font-light mt-1">{stats.won_count}</div>
            </CardContent></Card>
            <Card><CardContent className="p-4">
              <div className="flex items-center gap-2 text-muted-foreground text-xs"><IndianRupee className="h-3.5 w-3.5" />Won Value</div>
              <div className="text-2xl font-light mt-1">{fmtINR(stats.won_value)}</div>
            </CardContent></Card>
          </div>
        )}

        {loading ? <div className="animate-pulse space-y-3">{[1,2,3].map(i => <div key={i} className="h-32 bg-muted/40 rounded-xl" />)}</div> : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="crm-kanban">
            {STAGES.map(stage => {
              const stageLeads = leads.filter(l => l.stage === stage.key);
              return (
                <Card key={stage.key} className="border-2" data-testid={`stage-${stage.key}`}>
                  <CardContent className="p-4">
                    <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border text-xs font-medium ${stage.color}`}>
                      {stage.label} · {stageLeads.length}
                    </div>
                    <div className="space-y-2 mt-3 min-h-[60px]">
                      {stageLeads.length === 0 ? <p className="text-xs text-muted-foreground text-center py-4">No leads</p> :
                        stageLeads.map(lead => (
                          <div key={lead.lead_id} className="p-3 rounded-lg border bg-card hover:shadow-md transition-all cursor-pointer" data-testid={`lead-card-${lead.lead_id}`} onClick={() => openDetail(lead)}>
                            <div className="font-medium text-sm truncate">{lead.name}</div>
                            {lead.city && <div className="text-xs text-muted-foreground">{lead.city}{lead.property_type ? ` · ${lead.property_type}` : ''}</div>}
                            {lead.estimated_value > 0 && <div className="text-xs font-medium text-emerald-600 mt-1">{fmtINR(lead.estimated_value)}</div>}
                            {lead.next_follow_up && <div className="text-xs text-amber-600 mt-1 flex items-center gap-1"><CalendarIcon className="h-3 w-3" />Follow: {lead.next_follow_up}</div>}
                            <div className="flex items-center gap-2 mt-2">
                              {lead.phone && <a onClick={e => e.stopPropagation()} href={`tel:${lead.phone}`} className="p-1 rounded hover:bg-muted"><Phone className="h-3 w-3" /></a>}
                              {lead.email && <a onClick={e => e.stopPropagation()} href={`mailto:${lead.email}`} className="p-1 rounded hover:bg-muted"><Mail className="h-3 w-3" /></a>}
                              <Select value={lead.stage} onValueChange={(v) => handleStageChange(lead, v)}>
                                <SelectTrigger className="h-6 text-xs ml-auto w-24" onClick={e => e.stopPropagation()} data-testid={`stage-select-${lead.lead_id}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>{STAGES.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
                              </Select>
                            </div>
                          </div>
                        ))
                      }
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Create / Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit Lead' : 'New Lead'}</DialogTitle><DialogDescription>Track a prospect through your sales pipeline</DialogDescription></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Lead Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} required data-testid="lead-name-input" /></div>
              <div><Label>Contact Person</Label><Input value={form.contact_person} onChange={e => setForm({...form, contact_person: e.target.value})} /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} /></div>
              <div><Label>City</Label><Input value={form.city} onChange={e => setForm({...form, city: e.target.value})} /></div>
              <div><Label>Area (sqft)</Label><Input type="number" value={form.area_sqft} onChange={e => setForm({...form, area_sqft: e.target.value})} /></div>
              <div><Label>Source</Label>
                <Select value={form.source} onValueChange={v => setForm({...form, source: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{SOURCES.map(s => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label>Property Type</Label>
                <Select value={form.property_type} onValueChange={v => setForm({...form, property_type: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{PROPERTY_TYPES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label>Stage</Label>
                <Select value={form.stage} onValueChange={v => setForm({...form, stage: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{STAGES.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label>Assigned To</Label>
                <Select value={form.assigned_to} onValueChange={v => setForm({...form, assigned_to: v})}><SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger><SelectContent>{teamMembers.map(u => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}</SelectContent></Select>
              </div>
              <div><Label>Est. Budget (₹)</Label><Input type="number" value={form.estimated_budget} onChange={e => setForm({...form, estimated_budget: e.target.value})} /></div>
              <div><Label>Est. Value / Our Fee (₹)</Label><Input type="number" value={form.estimated_value} onChange={e => setForm({...form, estimated_value: e.target.value})} /></div>
              <div><Label>Timeline</Label><Input placeholder="e.g. Q3 2026" value={form.timeline} onChange={e => setForm({...form, timeline: e.target.value})} /></div>
              <div><Label>Next Follow-up</Label><Input type="date" value={form.next_follow_up} onChange={e => setForm({...form, next_follow_up: e.target.value})} /></div>
              <div className="sm:col-span-2"><Label>Tags (comma separated)</Label><Input placeholder="luxury, turn-key, south-mumbai" value={form.tags} onChange={e => setForm({...form, tags: e.target.value})} /></div>
              <div className="sm:col-span-2"><Label>Notes</Label><Textarea rows={3} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} /></div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={formLoading} data-testid="save-lead-btn">{formLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{editing ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selected && <>
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between gap-3">
                <span>{selected.name}</span>
                <span className={`text-xs px-2 py-1 rounded-full ${STAGES.find(s => s.key === selected.stage)?.color || ''}`}>{STAGES.find(s => s.key === selected.stage)?.label}</span>
              </DialogTitle>
              <DialogDescription>{selected.city} · {selected.property_type}</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-muted-foreground">Contact: </span>{selected.contact_person || '—'}</div>
                <div><span className="text-muted-foreground">Phone: </span>{selected.phone || '—'}</div>
                <div><span className="text-muted-foreground">Email: </span>{selected.email || '—'}</div>
                <div><span className="text-muted-foreground">Source: </span>{selected.source}</div>
                <div><span className="text-muted-foreground">Budget: </span>{fmtINR(selected.estimated_budget)}</div>
                <div><span className="text-muted-foreground">Est. Value: </span>{fmtINR(selected.estimated_value)}</div>
                <div><span className="text-muted-foreground">Timeline: </span>{selected.timeline || '—'}</div>
                <div><span className="text-muted-foreground">Follow-up: </span>{selected.next_follow_up || '—'}</div>
                <div><span className="text-muted-foreground">Assigned: </span>{selected.assigned_to_name || '—'}</div>
                <div><span className="text-muted-foreground">Tags: </span>{(selected.tags || []).join(', ') || '—'}</div>
              </div>
              {selected.notes && <div><div className="text-xs text-muted-foreground mb-1">Notes</div><div className="text-sm p-3 rounded-md bg-muted/30">{selected.notes}</div></div>}

              <div>
                <div className="text-xs text-muted-foreground mb-2">Activity Log</div>
                <div className="space-y-2 max-h-48 overflow-y-auto" data-testid="activity-log">
                  {(selected.activity_log || []).slice().reverse().map((a, i) => (
                    <div key={i} className="text-xs p-2 rounded border-l-2 border-violet-300 bg-violet-50/40">
                      <div className="font-medium">{a.user_name} · {a.action}</div>
                      <div className="text-muted-foreground">{a.note}</div>
                      <div className="text-muted-foreground/60 mt-1">{new Date(a.timestamp).toLocaleString()}</div>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2 mt-2">
                  <Input placeholder="Add note / activity..." value={activityNote} onChange={e => setActivityNote(e.target.value)} data-testid="activity-input" />
                  <Button onClick={addActivity} size="sm">Add</Button>
                </div>
              </div>

              {selected.converted_project_id && <div className="p-3 rounded-md bg-emerald-50 text-emerald-700 text-sm">✓ Converted to project: {selected.converted_project_id}</div>}
            </div>
            <DialogFooter className="gap-2 flex-wrap">
              <Button variant="outline" onClick={() => handleDelete(selected)} className="text-red-600"><Trash2 className="h-4 w-4 mr-1" />Delete</Button>
              <Button variant="outline" onClick={() => { setDetailOpen(false); openEdit(selected); }}><Pencil className="h-4 w-4 mr-1" />Edit</Button>
              {!selected.converted_project_id && <Button onClick={() => { setConvertName(`${selected.name} - Project`); setConvertOpen(true); }} data-testid="convert-lead-btn"><ArrowRightCircle className="h-4 w-4 mr-1" />Convert to Project</Button>}
            </DialogFooter>
          </>}
        </DialogContent>
      </Dialog>

      {/* Convert Dialog */}
      <Dialog open={convertOpen} onOpenChange={setConvertOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Convert Lead to Project</DialogTitle><DialogDescription>This marks the lead as Won and creates a new project.</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <Label>Project Name</Label>
            <Input value={convertName} onChange={e => setConvertName(e.target.value)} data-testid="convert-project-name" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConvertOpen(false)}>Cancel</Button>
            <Button onClick={handleConvert} data-testid="confirm-convert-btn">Create Project</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
